import React, { useCallback, useState } from 'react';

import UserSearchWidget, { SearchResult } from '~/components/Common/UserSearchWidget';

const SEARCH_LABEL = 'Search customer: name / email / phone / Société account / user ID';

interface CampaignCustomerSearchProps {
  /** Unique per dialog: the widget uses it as the input id */
  id: string;
  /** Called with the selected customer's id, or null when the search is cleared */
  onCustomerChange: (customerId: string | null) => void;
}

/**
 * Customer search for campaign dialogs (enrolments, approval requests). Uses the standard Admin
 * Portal user search, so it finds customers the same way as everywhere else, then narrows the
 * dialog's list to the selected customer.
 */
export default function CampaignCustomerSearch({ id, onCustomerChange }: CampaignCustomerSearchProps) {
  const [label, setLabel] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

  const handleSelectResult = useCallback(
    (result: SearchResult | null) => {
      if (result?.entityType !== 'user') {
        setSelectedEmail(null);
        onCustomerChange(null);
        return;
      }
      setSelectedEmail(result.email);
      setLabel(result.email);
      onCustomerChange(result.value);
    },
    [onCustomerChange],
  );

  // Editing the text after picking a customer drops the filter until a new customer is picked
  const handleSearchInputChange = useCallback(
    (value: string) => {
      setLabel(value);
      if (selectedEmail !== null && value.trim() !== selectedEmail.trim()) {
        setSelectedEmail(null);
        onCustomerChange(null);
      }
    },
    [onCustomerChange, selectedEmail],
  );

  const handleChange = useCallback(
    (_fieldId: string, value: string) => {
      if (!value) {
        setSelectedEmail(null);
        onCustomerChange(null);
      }
    },
    [onCustomerChange],
  );

  return (
    <UserSearchWidget
      id={id}
      label={SEARCH_LABEL}
      formErrors={[]}
      loading={false}
      onChange={handleChange}
      onSearchInputChange={handleSearchInputChange}
      onSelectResult={handleSelectResult}
      passedAutocompleteValue={label}
      setPassedAutocompleteValue={setLabel}
    />
  );
}
